import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Slider, Button, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import useStyles from './KaraokeStyles';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../store/slices/common';
import { uploadImage } from '../../http/uploads';

const AudioFileUpload = ({ setAudio, setAudioId, setDuration }) => {
    const classes = useStyles();
    const inputFileRef = useRef(null);
    const audioPreviewRef = useRef(null);
    const dispatch = useDispatch();
    const [isDragging, setIsDragging] = useState(false);
    const [originalFile, setOriginalFile] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [fullDuration, setFullDuration] = useState(0);
    const [trimStart, setTrimStart] = useState(0);
    const [trimEnd, setTrimEnd] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [showTrimControls, setShowTrimControls] = useState(false);
    const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);
    const sliderContainerRef = useRef(null);

    // Convert AudioBuffer to WAV format
    const audioBufferToWav = (buffer) => {
        const numChannels = buffer.numberOfChannels;
        const sampleRate = buffer.sampleRate;
        const format = 1; // PCM
        const bitDepth = 16;
        
        const bytesPerSample = bitDepth / 8;
        const blockAlign = numChannels * bytesPerSample;
        
        const dataLength = buffer.length * blockAlign;
        const bufferLength = 44 + dataLength;
        
        const arrayBuffer = new ArrayBuffer(bufferLength);
        const view = new DataView(arrayBuffer);
        
        // Write WAV header
        const writeString = (offset, string) => {
            for (let i = 0; i < string.length; i++) {
                view.setUint8(offset + i, string.charCodeAt(i));
            }
        };
        
        writeString(0, 'RIFF');
        view.setUint32(4, bufferLength - 8, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, format, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * blockAlign, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitDepth, true);
        writeString(36, 'data');
        view.setUint32(40, dataLength, true);
        
        // Write audio data
        const offset = 44;
        const channelData = [];
        for (let i = 0; i < numChannels; i++) {
            channelData.push(buffer.getChannelData(i));
        }
        
        let pos = 0;
        for (let i = 0; i < buffer.length; i++) {
            for (let ch = 0; ch < numChannels; ch++) {
                let sample = Math.max(-1, Math.min(1, channelData[ch][i]));
                sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
                view.setInt16(offset + pos, sample, true);
                pos += 2;
            }
        }
        
        return new Blob([arrayBuffer], { type: 'audio/wav' });
    };

    // Process audio with trim and fade-out
    const processAudioWithTrimAndFade = async (file, start, end, fadeDuration = 1) => {
        try {
            const arrayBuffer = await file.arrayBuffer();
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
            
            const sampleRate = audioBuffer.sampleRate;
            const startSample = Math.floor(start * sampleRate);
            const endSample = Math.floor(end * sampleRate);
            const numberOfChannels = audioBuffer.numberOfChannels;
            const croppedLength = endSample - startSample;
            
            // Create cropped buffer
            const croppedBuffer = audioContext.createBuffer(
                numberOfChannels,
                croppedLength,
                sampleRate
            );
            
            // Copy and apply fade-out
            const fadeStartSample = croppedLength - Math.floor(fadeDuration * sampleRate);
            
            for (let channel = 0; channel < numberOfChannels; channel++) {
                const sourceData = audioBuffer.getChannelData(channel);
                const targetData = croppedBuffer.getChannelData(channel);
                
                for (let i = 0; i < croppedLength; i++) {
                    let sample = sourceData[startSample + i];
                    
                    // Apply fade-out in the last fadeDuration seconds
                    if (i >= fadeStartSample) {
                        const fadeProgress = (i - fadeStartSample) / (croppedLength - fadeStartSample);
                        sample *= (1 - fadeProgress); // Fade from 1 to 0
                    }
                    
                    targetData[i] = sample;
                }
            }
            
            // Convert to WAV blob
            const wavBlob = audioBufferToWav(croppedBuffer);
            const processedFile = new File(
                [wavBlob],
                file.name.replace(/\.[^/.]+$/, '.wav'),
                { type: 'audio/wav' }
            );
            
            await audioContext.close();
            return processedFile;
        } catch (error) {
            console.error('Error processing audio:', error);
            throw error;
        }
    };

    const handleFileSelected = async (file) => {
        if (file && (file.type === 'audio/mp3' || file.type === 'audio/wav' || file.type === 'audio/mpeg')) {
            setOriginalFile(file);
            const url = URL.createObjectURL(file);
            setAudioUrl(url);
            
            const audio = new Audio(url);
            audio.onloadedmetadata = () => {
                const duration = audio.duration;
                setFullDuration(duration);
                setTrimStart(0);
                setTrimEnd(duration);
                setShowTrimControls(true);
            };
        } else {
            alert('Please upload a valid MP3 or WAV file');
        }
    };

    const processAudioFile = async (file) => {
        if (file && (file.type === 'audio/mp3' || file.type === 'audio/wav' || file.type === 'audio/mpeg')) {
            setAudio(file);
            const formData = new FormData()
            formData.append('file', file)
            formData.append('folder', 'karaoke')
            formData.append('metadata', JSON.stringify({ type: 'karaoke_audio' }))
            const audio = new Audio(URL.createObjectURL(file));
            audio.onloadedmetadata = () => {
                const audioDuration = audio.duration;
                setDuration(audioDuration)
            };
            try {
                dispatch(setLoading(true))
                const res = await uploadImage(formData);
                setAudioId(res?.data?.data?._id)
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(setLoading(false))
            }
        } else {
            alert('Please upload a valid MP3 or WAV file');
        }
    };

    const handleApplyTrim = async () => {
        if (!originalFile) return;
        
        try {
            dispatch(setLoading(true));
            const processedFile = await processAudioWithTrimAndFade(originalFile, trimStart, trimEnd);
            await processAudioFile(processedFile);
            setShowTrimControls(false);
            // Clean up
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        } catch (error) {
            console.error('Error applying trim:', error);
            alert('Error processing audio. Please try again.');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleCancelTrim = () => {
        setShowTrimControls(false);
        setOriginalFile(null);
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }
        if (inputFileRef.current) {
            inputFileRef.current.value = '';
        }
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            await handleFileSelected(file);
        }
    };

    // Audio preview controls
    useEffect(() => {
        if (audioPreviewRef.current && audioUrl && !isDraggingPlayhead) {
            const audio = audioPreviewRef.current;
            
            const updateTime = () => {
                if (!isDraggingPlayhead) {
                    setCurrentTime(audio.currentTime);
                    if (audio.currentTime >= trimEnd) {
                        audio.pause();
                        setIsPlaying(false);
                        audio.currentTime = trimStart;
                    }
                }
            };
            
            audio.addEventListener('timeupdate', updateTime);
            audio.addEventListener('ended', () => {
                setIsPlaying(false);
                audio.currentTime = trimStart;
            });
            
            return () => {
                audio.removeEventListener('timeupdate', updateTime);
            };
        }
    }, [audioUrl, trimStart, trimEnd, isDraggingPlayhead]);

    const handlePlayPause = () => {
        if (audioPreviewRef.current) {
            if (isPlaying) {
                audioPreviewRef.current.pause();
                setIsPlaying(false);
            } else {
                audioPreviewRef.current.currentTime = Math.max(trimStart, audioPreviewRef.current.currentTime);
                audioPreviewRef.current.play();
                setIsPlaying(true);
            }
        }
    };

    const handleSliderChange = (event, newValue) => {
        setTrimStart(newValue[0]);
        setTrimEnd(newValue[1]);
        if (audioPreviewRef.current) {
            if (audioPreviewRef.current.currentTime < newValue[0] || audioPreviewRef.current.currentTime > newValue[1]) {
                audioPreviewRef.current.currentTime = newValue[0];
            }
        }
    };

    // Calculate playhead position as percentage
    const getPlayheadPosition = () => {
        if (!fullDuration) return 0;
        return (currentTime / fullDuration) * 100;
    };

    // Handle playhead drag start
    const handlePlayheadMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingPlayhead(true);
        if (audioPreviewRef.current && isPlaying) {
            audioPreviewRef.current.pause();
            setIsPlaying(false);
        }
        // Immediately update position on click
        handlePlayheadDrag(e);
    };

    // Handle playhead drag
    const handlePlayheadDrag = (e) => {
        if (!sliderContainerRef.current || !audioPreviewRef.current) return;
        
        const rect = sliderContainerRef.current.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
        if (clientX === undefined) return;
        
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
        const newTime = (percentage / 100) * fullDuration;
        
        // Clamp to trim range
        const clampedTime = Math.max(trimStart, Math.min(trimEnd, newTime));
        setCurrentTime(clampedTime);
        audioPreviewRef.current.currentTime = clampedTime;
    };

    // Handle playhead drag end
    const handlePlayheadMouseUp = () => {
        setIsDraggingPlayhead(false);
    };

    // Add global mouse and touch event listeners for dragging
    useEffect(() => {
        if (isDraggingPlayhead && sliderContainerRef.current && audioPreviewRef.current) {
            const handleMouseMove = (e) => {
                if (!sliderContainerRef.current || !audioPreviewRef.current) return;
                
                const rect = sliderContainerRef.current.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                const newTime = (percentage / 100) * fullDuration;
                
                // Clamp to trim range
                const clampedTime = Math.max(trimStart, Math.min(trimEnd, newTime));
                setCurrentTime(clampedTime);
                audioPreviewRef.current.currentTime = clampedTime;
            };
            
            const handleTouchMove = (e) => {
                e.preventDefault();
                if (!sliderContainerRef.current || !audioPreviewRef.current) return;
                
                const rect = sliderContainerRef.current.getBoundingClientRect();
                const clientX = e.touches?.[0]?.clientX;
                if (clientX === undefined) return;
                
                const x = clientX - rect.left;
                const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
                const newTime = (percentage / 100) * fullDuration;
                
                // Clamp to trim range
                const clampedTime = Math.max(trimStart, Math.min(trimEnd, newTime));
                setCurrentTime(clampedTime);
                audioPreviewRef.current.currentTime = clampedTime;
            };
            
            const handleMouseUp = () => setIsDraggingPlayhead(false);
            const handleTouchEnd = () => setIsDraggingPlayhead(false);
            
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleTouchEnd);
            
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
                window.removeEventListener('touchmove', handleTouchMove);
                window.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isDraggingPlayhead, fullDuration, trimStart, trimEnd]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleClick = () => {
        if (inputFileRef.current) {
            inputFileRef.current.click();
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            await handleFileSelected(file);
        }
    };

    return (
        <Box className={classes.root}>
            <Typography className={classes.commandsTitle} gutterBottom>
                Upload your instrumental backing track to sync with lyrics
            </Typography>
            
            {!showTrimControls ? (
                <Box
                    className={classes.dropzone}
                    onClick={handleClick}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                        backgroundColor: isDragging ? 'rgba(20, 44, 92, 0.1)' : 'transparent',
                        transition: 'background-color 0.2s ease',
                    }}
                >
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <CloudUploadIcon className={classes.icon} />
                        <Typography variant="body1" className={classes.instructions}>
                            <strong>Click to upload</strong> or drag and drop
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            MP3, WAV
                        </Typography>
                    </Box>
                    <input
                        ref={inputFileRef}
                        type="file"
                        accept=".mp3,.wav"
                        onChange={handleAudioUpload}
                        style={{ display: 'none' }}
                    />
                </Box>
            ) : (
                <Box sx={{ width: '100%', maxWidth: '50rem' }}>
                    <audio ref={audioPreviewRef} src={audioUrl} style={{ display: 'none' }} />
                    
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={handlePlayPause}
                            sx={{
                                backgroundColor: '#142C5C',
                                color: 'white',
                                '&:hover': { backgroundColor: '#0f2247' }
                            }}
                        >
                            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                        <Typography variant="body2" sx={{ minWidth: '100px' }}>
                            {formatTime(currentTime)} / {formatTime(trimEnd)}
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            Trim audio: {formatTime(trimStart)} - {formatTime(trimEnd)} 
                            (Duration: {formatTime(trimEnd - trimStart)})
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                            * Audio will automatically fade out in the last 1 second
                        </Typography>
                        <Box 
                            ref={sliderContainerRef}
                            sx={{ 
                                position: 'relative',
                                width: '100%',
                                py: 1,
                                cursor: isDraggingPlayhead ? 'grabbing' : 'default'
                            }}
                        >
                            <Slider
                                value={[trimStart, trimEnd]}
                                min={0}
                                max={fullDuration}
                                onChange={handleSliderChange}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(value) => formatTime(value)}
                                sx={{
                                    color: '#142C5C',
                                    '& .MuiSlider-thumb': {
                                        color: '#142C5C',
                                    },
                                }}
                            />
                            {/* Draggable Playhead Indicator */}
                            <Box
                                onMouseDown={handlePlayheadMouseDown}
                                onTouchStart={handlePlayheadMouseDown}
                                sx={{
                                    position: 'absolute',
                                    left: `${getPlayheadPosition()}%`,
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '4px',
                                    height: '32px',
                                    backgroundColor: '#ff5722',
                                    borderRadius: '2px',
                                    cursor: isDraggingPlayhead ? 'grabbing' : 'grab',
                                    zIndex: 10,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                    transition: isDraggingPlayhead ? 'none' : 'left 0.1s linear',
                                    pointerEvents: 'auto',
                                    '&:hover': {
                                        width: '6px',
                                        height: '36px',
                                        backgroundColor: '#ff7043',
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        backgroundColor: '#ff5722',
                                        border: '2px solid white',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                    }
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<CheckIcon />}
                            onClick={handleApplyTrim}
                            className={classes.buttonStyle}
                            disabled={trimEnd <= trimStart}
                        >
                            Apply & Upload
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={handleCancelTrim}
                            sx={{
                                borderColor: '#142C5C',
                                color: '#142C5C',
                                '&:hover': {
                                    borderColor: '#0f2247',
                                    backgroundColor: 'rgba(20, 44, 92, 0.1)',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default AudioFileUpload;
